import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import { Container, Switch, FormControlLabel } from "@mui/material";
import { TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, Delete, Edit, Search } from "@mui/icons-material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ResponsiveDialogManager from "../../components/dialog/ResponsiveDialogManager";
import ToolbarDefaultActions from "../../components/dialog/toolbarDefaultActions";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import ConfirmationDialog from "../../components/dialog/ConfirmationDialog";
import { EntityService } from "../../services";
import EntityForm from "../../components/forms/EntityForm";
import { useDispatch } from "react-redux";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockIcon from "@mui/icons-material/Lock";
import CopyToClipboard from "react-copy-to-clipboard";
import { QRCode } from "react-qrcode-logo";
import LinkIcon from "@mui/icons-material/Link";
import { pictoWebVr } from "../../helpers";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { saveRequest } from "../../reducers/entity/entitySlice";

const PREFIX = "AdminEntities";

const classes = {
  marginBottom: `${PREFIX}-marginBottom`,
  filter: `${PREFIX}-filter`,
  filterButtons: `${PREFIX}-filterButtons`,
  mobileMarginBottom: `${PREFIX}-mobileMarginBottom`,
  qrRow: `${PREFIX}-qrRow`,
  QrContainer: `${PREFIX}-QrContainer`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.marginBottom}`]: {
    marginBottom: "0.5rem",
  },

  [`& .${classes.filter}`]: {
    display: "flex",
    justifyContent: "space-around",
  },

  [`& .${classes.filterButtons}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "1rem",
    marginTop: "1rem",
  },

  [`&.${classes.mobileMarginBottom}`]: {
    [theme.breakpoints.between("xs", "md")]: {
      marginBottom: "5rem",
    },
  },

  [`& .${classes.qrRow}`]: {
    display: "flex",
    width: "100%",
  },

  [`& .${classes.QrContainer}`]: {
    display: "none",
  },
}));

export const AdminEntities = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [entities, setEntities] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [name, setName] = React.useState("");
  const [create, setCreate] = React.useState(false);
  const [copied, setCopied] = useState(false);
  const [picto, setPicto] = useState(null);
  const [idToDelete, setIdToDelete] = React.useState(null);
  const [idToUpdate, setIdToUpdate] = React.useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    setPicto(pictoWebVr);
  }, []);

  const canvasRefs = useRef({});

  const downloadQR = (id, name) => {
    let canvas = canvasRefs.current[id].querySelector("canvas");
    let image = canvas.toDataURL("image/jpg");
    let anchor = document.createElement("a");
    anchor.href = image;
    const finalName = name.replace(" ", "-");
    anchor.download = `QRCode-${finalName}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const columns = (editEntity, confirmDelete, t) => [
    { field: "id", headerName: "Id", width: 70, type: "string", hide: true },
    {
      field: "logo",
      headerName: "Logo",
      width: 120,
      renderCell: (params) =>
        params.row &&
        params.row.logo && (
          <img src={params.row.logo} height={50} alt="sphere logo" />
        ),
    },
    { field: "name", headerName: t("entities.name"), width: 250 },
    {
      field: "action",
      headerName: t("entities.actions"),
      width: 130,
      renderCell: (params) => (
        <div>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              editEntity(params.row.id);
            }}
          >
            <Edit />
          </IconButton>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleVisibleChange(params.row)}
          >
            {params.row.visible ? <Visibility /> : <VisibilityOff />}
          </IconButton>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => confirmDelete(params.row.id)}
          >
            <Delete />
          </IconButton>
        </div>
      ),
    },
    {
      field: "published",
      headerName: t("entities.published"),
      width: 130,
      renderCell: (params) => (
        <div>
          <FormControlLabel
            control={
              <Switch
                checked={params.row.published}
                onChange={() => handlePublishedChange(params.row)}
                name="checkedB"
                color="primary"
              />
            }
          />
        </div>
      ),
    },
    {
      field: "qrcode",
      headerName: t("entities.qrCode"),
      width: 200,
      renderCell: (params) => {
        return (
          <div className={classes.qrRow}>
            <CopyToClipboard
              text={params.row.arLink}
              onCopy={() => {
                setCopied(true);
                enqueueSnackbar(t("toasts.copiedInClipboard"), {
                  variant: "info",
                });
              }}
            >
              <IconButton
                variant="contained"
                color="primary"
                className={classes.shareIconButton}
                size="large"
              >
                <FileCopyIcon />
              </IconButton>
            </CopyToClipboard>
            <div
              ref={(el) => (canvasRefs.current[params.row._id] = el)}
              className={classes.QrContainer}
            >
              <QRCode
                id="react-qrcode-logo"
                value={params.row.arLink}
                size={250}
                ecLevel="H"
                bgColor="#000000"
                fgColor="#D3AF5F" //golden
                includeMargin={true}
                qrStyle="dots"
                logoImage={picto}
                logoHeight={65}
                logoWidth={65}
                eyeRadius={[
                  [0, 100, 100, 100],
                  [100, 0, 100, 100],
                  [100, 100, 100, 0],
                ]}
                logoOpacity={1} // the higher the opacity is, the lower the logo has to be
              />
            </div>
            <div className={classes.buttonsQrCode}>
              <a
                href={params.row.arLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconButton
                  variant="contained"
                  color="primary"
                  className={classes.shareIconButton}
                  size="large"
                >
                  <LinkIcon />
                </IconButton>
              </a>
              <IconButton
                variant="contained"
                color="primary"
                className={classes.shareIconButton}
                onClick={() => downloadQR(params.row._id, params.row.name)}
                size="large"
              >
                <GetAppIcon />
              </IconButton>
            </div>
          </div>
        );
      },
    },
    {
      field: "secured",
      headerName: t("entities.securedByPassword"),
      width: 130,
      renderCell: (params) => (
        <React.Fragment>
          {params.row.isSecuredByPassword && <LockIcon color="primary" />}
        </React.Fragment>
      ),
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setLoading(true);
    EntityService.getEntities("sphere")
      .then((result) => {
        setEntities(() => result.data.reverse());
      })
      .finally(() => setLoading(false));
  };

  const handleVisibleChange = (item) => {
    EntityService.updateVisible(item.id, !item.visible).then(() => {
      enqueueSnackbar(t("toasts.entityUpdateSuccess"), { variant: "success" });
      loadData();
    });
  };

  const handlePublishedChange = (item) => {
    EntityService.updatePublish(item.id, !item.published).then(() => {
      enqueueSnackbar(t("toasts.entityUpdateSuccess"), { variant: "success" });
      loadData();
    });
  };

  const search = () => {
    setLoading(true);
    EntityService.search(name)
      .then((result) => {
        setEntities(() => result);
      })
      .finally(() => setLoading(false));
  };

  const reinit = async () => {
    await setName(() => "");
    loadData();
  };

  const creation = () => {
    setCreate(true);
  };

  const save = (data) => {
    if (data._id && data._id.length > 0) {
      EntityService.updateEntity(data)
        .then((result) => {
          setIdToUpdate(null);
          setCreate(false);
          enqueueSnackbar(`${t("toasts.entityUpdateSuccess")}`, {
            variant: "success",
          });
          loadData();
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    } else {
      EntityService.createEntity(data)
        .then(() => {
          setCreate(false);
          setIdToUpdate(null);
          enqueueSnackbar(`${data.name} ${t("toasts.entityCreateSuccess")}`, {
            variant: "success",
          });
          loadData();
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    }
  };

  const _delete = () => {
    EntityService.deleteEntity(idToDelete)
      .then((result) => {
        enqueueSnackbar(
          `${entities.find((e) => e.id === idToDelete).name} ${t(
            "toasts.entityDeleteSuccess"
          )}`,
          { variant: "success" }
        );
        loadData();
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      })
      .finally(() => setIdToDelete(null));
  };

  const confirmDelete = (id) => {
    setIdToDelete(id);
  };

  const editEntity = (id) => {
    setIdToUpdate(id);
  };

  const setOpen = (value) => {
    if (!value) setIdToDelete(null);
  };

  return (
    <StyledDiv>
      <Container className={classes.mobileMarginBottom}>
        <h1>{t("entities.title")}</h1>
        <div>
          <h4 className={classes.marginBottom}>{t("entities.filters")}</h4>
          <div className={classes.filter}>
            <TextField
              variant="outlined"
              margin="normal"
              id="name"
              label={t("entities.name")}
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className={classes.filterButtons}>
            <Button onClick={search} variant="contained" color="primary">
              <Search />
              {t("entities.buttons.search")}
            </Button>
            <Button
              onClick={reinit}
              variant="contained"
              style={{ marginLeft: "1rem" }}
            >
              {t("entities.buttons.init")}
            </Button>
          </div>
          <div>
            <IconButton
              variant="contained"
              color="primary"
              onClick={creation}
              size="large"
            >
              <Add />
            </IconButton>
          </div>
        </div>
        <DataGrid
          rows={entities}
          columns={columns(editEntity, confirmDelete, t)}
          autoHeight
          disableRowSelectionOnClick
          pagination
          loading={loading}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[5, 10, 25, 50]}
        />
        <ResponsiveDialogManager
          onClose={() => setCreate(false)}
          open={create}
          renderer={<EntityForm save={save} cancel={() => setCreate(false)} />}
          title={t("entities.creation")}
          toolbarActions={
            <ToolbarDefaultActions
              save={() => dispatch(saveRequest())}
              cancel={() => setCreate(false)}
            />
          }
        />
        <ResponsiveDialogManager
          onClose={() => setIdToUpdate(null)}
          open={idToUpdate != null}
          renderer={
            <EntityForm
              save={save}
              entityId={idToUpdate}
              cancel={() => setIdToUpdate(null)}
            />
          }
          title={t("entities.update")}
          toolbarActions={
            <ToolbarDefaultActions
              save={() => dispatch(saveRequest())}
              cancel={() => setIdToUpdate(null)}
            />
          }
        />
        <ConfirmationDialog
          open={idToDelete != null}
          onAgree={_delete}
          element={
            idToDelete != null
              ? entities.find((u) => u.id === idToDelete)
              : null
          }
          setOpen={setOpen}
          mode="deleteEntity"
        />
      </Container>
    </StyledDiv>
  );
};
